import { DeleteOutlined, SearchOutlined, SettingOutlined } from '@ant-design/icons';
import { Button, Input, Space, Tag } from 'antd';
import { observer } from 'mobx-react';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Component from '../../../../shared/components';
import { TableColumnInterface } from '../../../../shared/type/tableType';
import { titleAutoGeneratorService } from '../service/titleAutoGeneratorService';
import { TitleAutoGenerator } from '../type/titleAutoGeneratorType';

const TitleAutoGeneratorList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams({
    page: '1',
    page_size: '10',
    order_by: 'id',
    order_direction: 'desc',
  });
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [_selectedList, setSelectedList] = useState<number[]>([]);
  const [slugSearchValue, setSlugSearchValue] = useState<string>(
    searchParams.get('search') || ''
  );

  const columns: TableColumnInterface<TitleAutoGenerator>[] = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '5%',
      sorter: true,
      render: (text: string, _record: TitleAutoGenerator): JSX.Element => <div>{text}</div>,
    },
    {
      title: 'SLUG',
      dataIndex: 'slug',
      key: 'slug',
      width: '20%',
      sorter: true,
      render: (text: string, record: TitleAutoGenerator): JSX.Element => (
        <a
          href="#"
          onClick={e => {
            e.preventDefault();
            window.open(`/title-auto-generator/edit/${record.id}`, '_blank');
          }}
          style={{ color: '#1890ff' }}
        >
          {text}
        </a>
      ),
    },
    {
      title: 'AUTOMATION TYPE',
      dataIndex: 'automation_type',
      key: 'automation_type',
      width: '15%',
      filters: [
        { text: 'Private', value: 'private' },
        { text: 'Episode Number', value: 'episode_no' },
        { text: 'Date', value: 'date' },
      ],
      filteredValue: searchParams.get('automation_type')
        ? searchParams.get('automation_type')?.split(',')
        : null,
      render: (_: string, record: TitleAutoGenerator): JSX.Element => (
        <Tag color="blue">{record.automation_type_display}</Tag>
      ),
    },
    {
      title: 'NOTE',
      dataIndex: 'note',
      key: 'note',
      width: '25%',
      render: (text: string | null, _record: TitleAutoGenerator): JSX.Element => (
        <div style={{ wordBreak: 'break-word' }}>{text || '-'}</div>
      ),
    },
    {
      title: 'CREATED',
      dataIndex: 'created',
      key: 'created',
      width: '10%',
      sorter: true,
      render: (text: string, _record: TitleAutoGenerator): string =>
        moment(text).format('YYYY-MM-DD'),
    },
    {
      title: 'MODIFIED',
      dataIndex: 'modified',
      key: 'modified',
      width: '10%',
      sorter: true,
      render: (text: string, _record: TitleAutoGenerator): string =>
        moment(text).format('YYYY-MM-DD'),
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      dataIndex: 'actions',
      width: '10%',
      render: (_text: string, record: TitleAutoGenerator): JSX.Element => (
        <Space>
          <Button
            icon={<SettingOutlined />}
            onClick={() => window.open(`/title-auto-generator/edit/${record.id}`, '_blank')}
            className="action-btn"
          />
          <Button
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            className="action-btn"
            danger
          />
        </Space>
      ),
    },
  ];

  const handleDelete = async (record: TitleAutoGenerator) => {
    if (window.confirm(`Are you sure you want to delete "${record.slug}"?`)) {
      try {
        await titleAutoGeneratorService.delete(record.id);
        setRefreshCounter(prev => prev + 1);
      } catch (error) {
        console.error('Failed to delete:', error);
        alert('Failed to delete.');
      }
    }
  };

  const fetchData = useCallback(
    async (params: URLSearchParams): Promise<{ data: TitleAutoGenerator[]; count: number }> => {
      try {
        const result = await titleAutoGeneratorService.fetch(params);
        return { data: result.results, count: result.count };
      } catch (error) {
        console.error('Failed to fetch title auto generators:', error);
        return { data: [], count: 0 };
      }
    },
    []
  );

  useEffect(() => {
    setRefreshCounter(prev => prev + 1);
  }, [searchParams]);

  useEffect(() => {
    // Sync input value when URL parameters change
    setSlugSearchValue(searchParams.get('search') || '');
  }, [searchParams]);

  const handleSlugSearch = (value: string) => {
    setSearchParams(prev => {
      if (value.trim()) {
        prev.set('search', value.trim());
      } else {
        prev.delete('search');
      }
      prev.set('page', '1');
      return prev;
    });
  };

  return (
    <Component.BasePageComponent>
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Input
            placeholder="Search by Slug"
            prefix={<SearchOutlined />}
            value={slugSearchValue}
            onChange={e => setSlugSearchValue(e.target.value)}
            onKeyUp={e => {
              if (e.key === 'Enter') {
                handleSlugSearch((e.target as HTMLInputElement).value);
              }
            }}
            style={{ width: '300px' }}
            allowClear
            onClear={() => {
              setSlugSearchValue('');
              handleSlugSearch('');
            }}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={() => handleSlugSearch(slugSearchValue)}
          >
            Search
          </Button>
        </div>
      </div>
      <Component.TableComponent<TitleAutoGenerator>
        key={refreshCounter}
        name="Title Auto Generator"
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        dataFetch={fetchData}
        columns={columns}
        options={{
          isDisableIdColumn: true,
        }}
        actions={{
          onSelectionChange: setSelectedList,
          onAddClick: () => navigate('/title-auto-generator/add'),
        }}
      />
    </Component.BasePageComponent>
  );
};

export default observer(TitleAutoGeneratorList);
